import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { getIndustries } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function IndustriesIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    getIndustries().then(setItems).catch(() => {});
  }, []);
  useSEO({
    title: "Industries — Sector-Shaped Security | RockLadder Technologies",
    description: "Regulator-aware cybersecurity for BFSI, healthcare, energy, retail, aviation, and media & technology.",
    canonical: "https://rockladder.com/industries",
  });

  return (
    <>
      <PageHero
        eyebrow="INDUSTRIES WE SECURE"
        title="Regulator-aware."
        gradient="Sector-shaped."
        subtitle="Six deeply-served verticals, each with dedicated detection libraries, compliance playbooks, and threat models."
        breadcrumb={[{ label: "Industries" }]}
      />

      <section className="relative py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 md:gap-x-10 gap-y-12 md:gap-y-16">
            {items.map((it, i) => (
              <motion.div
                key={it.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.06 }}
              >
                <Link
                  to={`/industries/${it.slug}`}
                  data-testid={`industry-index-${it.slug}`}
                  className="group block"
                >
                  {/* Editorial monogram — 3:2 warm off-white, giant Fraunces initial */}
                  <div
                    className="relative overflow-hidden"
                    style={{
                      aspectRatio: "3 / 2",
                      background: "#F5F5F1",
                    }}
                  >
                    {/* Center monogram */}
                    <div className="absolute inset-0 grid place-items-center">
                      <span
                        className="font-editorial-display leading-none transition-opacity duration-500"
                        style={{
                          fontStyle: "italic",
                          fontWeight: 400,
                          fontSize: "clamp(140px, 18vw, 220px)",
                          color: "rgba(11,11,16,0.20)",
                          fontVariationSettings: '"SOFT" 30, "opsz" 144',
                        }}
                      >
                        {it.name.charAt(0)}
                      </span>
                    </div>

                    {/* Top-left label */}
                    <div className="absolute top-4 left-4 font-editorial-mono text-[10px] tracking-[0.32em] text-[#8B8B95]">
                      INDUSTRY
                    </div>

                    {/* Bottom-right arrow */}
                    <div className="absolute bottom-4 right-4 text-[#5B2A86] transition-opacity duration-500 opacity-40 group-hover:opacity-100">
                      <ArrowUpRight className="h-5 w-5" strokeWidth={1.4} />
                    </div>

                    {/* Bottom hairline that draws on hover */}
                    <span
                      aria-hidden
                      className="absolute bottom-0 left-0 h-[1.5px] bg-[#5B2A86] w-0 group-hover:w-full transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
                    />
                  </div>

                  {/* Meta */}
                  <div className="pt-6">
                    <h3
                      className="font-editorial-display text-[#0B0B10] group-hover:text-[#5B2A86] transition-colors duration-300"
                      style={{
                        fontSize: 26,
                        lineHeight: 1.1,
                        letterSpacing: "-0.02em",
                        fontWeight: 400,
                      }}
                    >
                      {it.name}
                    </h3>
                    <p className="mt-3 text-[14.5px] leading-[1.55] text-[#4A4A55] line-clamp-3 max-w-[420px]">
                      {it.short_desc}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-x-4 gap-y-1">
                      {(it.compliance_focus || []).slice(0, 3).map((c) => (
                        <span
                          key={c}
                          className="font-editorial-mono text-[9.5px] tracking-[0.24em] text-[#8B8B95]"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {items.length === 0 && (
            <div className="py-24 text-center text-[#4A4A55] text-sm">Loading industries…</div>
          )}
        </div>
      </section>
    </>
  );
}
